import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import apiClient from '../stateManagers/services/axiosInterceptor';
import CustomPaginator from '../components/CustomPaginator';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AppVersion {
  id: string;
  version_name: string;
  version_code: number;
  package_url: string;
  release_notes?: string;
  app_type: 'playstore' | 'non_playstore';
  is_update_released: boolean;
  released_at?: string;
  created_at: string;
}

interface ApiResponse {
  status: string;
  message?: string;
  versions: AppVersion[];
  count: number;
}

interface FormData {
  version_name: string;
  version_code: number | null;
  package_url: string;
  release_notes: string;
  app_type: string;
  is_update_released: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const APP_TYPE_OPTIONS = [
  { label: 'Play Store', value: 'playstore' },
  { label: 'Non Play Store', value: 'non_playstore' },
];

const INITIAL_FORM: FormData = {
  version_name: '',
  version_code: null,
  package_url: '',
  release_notes: '',
  app_type: 'non_playstore',
  is_update_released: false,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AppVersionsManager() {
  const [allVersions, setAllVersions] = useState<AppVersion[]>([]);
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [editingVersion, setEditingVersion] = useState<AppVersion | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppVersion | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const toast = useRef<Toast>(null);

  useEffect(() => {
    fetchVersions();
  }, []);

  useEffect(() => {
    const start = (page - 1) * pageSize;
    setVersions(allVersions.slice(start, start + pageSize));
  }, [page, pageSize, allVersions]);

  const totalRecords = allVersions.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchVersions = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiClient.get<ApiResponse>('/app-versions');
      if (response.data.status === 'success') {
        const list = response.data.versions || [];
        setAllVersions(list);
        setPage(1);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to load app versions',
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Form Helpers ────────────────────────────────────────────────────────────

  const handleInputChange = (field: keyof FormData, value: string | boolean | number | null): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = (): void => setFormData(INITIAL_FORM);

  const handleOpenAdd = (): void => {
    setEditingVersion(null);
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (rowData: AppVersion): void => {
    setEditingVersion(rowData);
    setFormData({
      version_name: rowData.version_name,
      version_code: rowData.version_code,
      package_url: rowData.package_url,
      release_notes: rowData.release_notes || '',
      app_type: rowData.app_type,
      is_update_released: Boolean(rowData.is_update_released),
    });
    setShowModal(true);
  };

  const handleModalClose = (): void => {
    setShowModal(false);
    setEditingVersion(null);
    resetForm();
  };

  // ── Toggle Release ─────────────────────────────────────────────────────────

  const handleToggleRelease = async (rowData: AppVersion): Promise<void> => {
    try {
      const newVal = !rowData.is_update_released;
      await apiClient.put(`/app-versions/${rowData.id}/release`, {
        is_update_released: newVal,
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Updated',
        detail: newVal ? 'Update marked as released' : 'Update marked as unreleased',
        life: 2000,
      });
      fetchVersions();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to update release status',
        life: 3000,
      });
    }
  };

  // ── Submit (Create / Update) ───────────────────────────────────────────────

  const handleSubmit = async (): Promise<void> => {
    if (!formData.version_name.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Version name is required', life: 3000 });
      return;
    }
    if (formData.version_code === null || formData.version_code === undefined) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Version code is required', life: 3000 });
      return;
    }
    if (!formData.package_url.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Package URL is required', life: 3000 });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        version_name: formData.version_name,
        version_code: formData.version_code,
        package_url: formData.package_url,
        release_notes: formData.release_notes,
        app_type: formData.app_type,
        is_update_released: formData.is_update_released,
      };

      const response = editingVersion
        ? await apiClient.put(`/app-versions/${editingVersion.id}`, payload)
        : await apiClient.post('/app-versions', payload);

      if (response.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: editingVersion ? 'Version updated successfully!' : 'Version added successfully!',
          life: 3000,
        });
        handleModalClose();
        setTimeout(() => fetchVersions(), 500);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to save version',
        life: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await apiClient.delete(`/app-versions/${deleteTarget.id}`);
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: `Version ${deleteTarget.version_name} deleted successfully`,
        life: 2500,
      });
      setDeleteTarget(null);
      fetchVersions();
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to delete version',
        life: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Column Templates ───────────────────────────────────────────────────────

  const versionNameTemplate = (rowData: AppVersion) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'linear-gradient(135deg,rgba(59,130,246,.12),rgba(37,99,235,.08))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className="pi pi-mobile" style={{ fontSize: 14, color: '#2563eb' }} />
      </div>
      <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937' }}>{rowData.version_name}</span>
    </div>
  );

  const versionCodeTemplate = (rowData: AppVersion) => (
    <span
      style={{
        background: '#f9fafb',
        color: '#1f2937',
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'monospace',
        border: '1px solid #e5e7eb',
        display: 'inline-block',
      }}
    >
      {rowData.version_code}
    </span>
  );

  const appTypeTemplate = (rowData: AppVersion) => {
    const isPlaystore = rowData.app_type === 'playstore';
    return (
      <span
        style={{
          background: isPlaystore ? '#dcfce7' : '#fef3c7',
          color: isPlaystore ? '#16a34a' : '#d97706',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <i className={isPlaystore ? 'pi pi-google' : 'pi pi-box'} style={{ fontSize: 11 }} />
        {isPlaystore ? 'Play Store' : 'Non Play Store'}
      </span>
    );
  };

  const releaseStatusTemplate = (rowData: AppVersion) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <InputSwitch
        checked={Boolean(rowData.is_update_released)}
        onChange={() => handleToggleRelease(rowData)}
      />
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: rowData.is_update_released ? '#16a34a' : '#9ca3af',
        }}
      >
        {rowData.is_update_released ? 'Released' : 'Pending'}
      </span>
    </div>
  );

  const packageUrlTemplate = (rowData: AppVersion) => (
    <a
      href={rowData.package_url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#3b82f6',
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: 220,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.textDecoration = 'none';
      }}
    >
      <i className="pi pi-external-link" style={{ fontSize: 11, flexShrink: 0 }} />
      <span
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {rowData.package_url.length > 40
          ? rowData.package_url.substring(0, 40) + '…'
          : rowData.package_url}
      </span>
    </a>
  );

  const releaseNotesTemplate = (rowData: AppVersion) => {
    if (!rowData.release_notes) {
      return <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>;
    }
    return (
      <div
        style={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 13,
          color: '#6b7280',
          fontWeight: 500,
        }}
        title={rowData.release_notes}
      >
        {rowData.release_notes}
      </div>
    );
  };

  const createdAtTemplate = (rowData: AppVersion) => (
    <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
      {new Date(rowData.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })}
    </div>
  );

  const actionsTemplate = (rowData: AppVersion) => (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-rounded"
        tooltip="Edit version"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#3b82f6', width: 32, height: 32 }}
        onClick={() => handleOpenEdit(rowData)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-rounded"
        tooltip="Delete version"
        tooltipOptions={{ position: 'top' }}
        style={{ color: '#ef4444', width: 32, height: 32 }}
        onClick={() => setDeleteTarget(rowData)}
      />
    </div>
  );

  // ── Summary Counts ─────────────────────────────────────────────────────────

  const playstoreCount = allVersions.filter((v) => v.app_type === 'playstore').length;
  const nonPlaystoreCount = allVersions.filter((v) => v.app_type === 'non_playstore').length;
  const releasedCount = allVersions.filter((v) => v.is_update_released).length;

  // ── Stat cards ─────────────────────────────────────────────────────────────

  const statCards = [
    {
      label: 'Total Versions',
      value: totalRecords,
      icon: 'pi-mobile',
      tint: 'rgba(59,130,246,.12)',
      iconColor: '#2563eb',
    },
    {
      label: 'Play Store',
      value: playstoreCount,
      icon: 'pi-google',
      tint: 'rgba(16,185,129,.12)',
      iconColor: '#059669',
    },
    {
      label: 'Non Play Store',
      value: nonPlaystoreCount,
      icon: 'pi-box',
      tint: 'rgba(245,158,11,.12)',
      iconColor: '#d97706',
    },
    {
      label: 'Released',
      value: releasedCount,
      icon: 'pi-check-circle',
      tint: 'rgba(16,185,129,.12)',
      iconColor: '#16a34a',
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif', background: '#f8fafc' }}>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(-10px) } to { opacity:1; transform:translateX(0) } }
          @keyframes fadeInSlide { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }
          .p-datatable .p-datatable-tbody > tr { animation:slideIn .35s ease-out; transition:background .15s; }
          .p-datatable .p-datatable-tbody > tr:hover { background:rgba(65,105,225,.04)!important; box-shadow:inset 4px 0 0 0 #4169E1; }
          .p-button-text:hover { background:#f3f4f6!important; }
          .p-datatable .p-datatable-thead > tr > th {
            background:#f9fafb!important;
            color:#6b7280!important;
            font-size:11px!important;
            font-weight:700!important;
            text-transform:uppercase!important;
            letter-spacing:.8px!important;
            padding:16px 12px!important;
            border-bottom:1px solid #e5e7eb!important;
          }
          .p-datatable .p-datatable-tbody > tr > td {
            padding:16px 12px!important;
            border-bottom:1px solid #f3f4f6!important;
          }
          .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
          .p-datatable .p-datatable-emptymessage td { padding:0!important; }
          .header-section { animation: fadeInSlide .5s ease-out; }
          .table-section { animation: fadeInSlide .55s ease-out; }
          .p-dialog .p-dialog-header {
            background: linear-gradient(135deg, #2855ae 0%, #3b82f6 100%) !important;
            color: white !important;
            border-radius: 20px 20px 0 0 !important;
            padding: 24px !important;
          }
          .p-dialog .p-dialog-header .p-dialog-title { color: white !important; font-weight: 600 !important; font-size: 20px !important; }
          .p-dialog .p-dialog-header .p-dialog-header-icon { color: white !important; }
          .p-dialog .p-dialog-content { padding: 24px !important; border-radius: 0 0 20px 20px !important; }
          .p-inputtext, .p-inputtextarea, .p-inputnumber-input {
            border: 2px solid #e5e7eb !important;
            border-radius: 10px !important;
            padding: 10px 14px !important;
            font-size: 14px !important;
            transition: all 0.2s !important;
          }
          .p-inputtext:focus, .p-inputtextarea:focus, .p-inputnumber-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important;
          }
          .p-dropdown { border: 2px solid #e5e7eb !important; border-radius: 10px !important; width: 100% !important; }
          .p-dropdown:not(.p-disabled).p-focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important; }
          .p-inputswitch.p-inputswitch-checked .p-inputswitch-slider { background: #16a34a !important; }
        `}</style>

        {/* ── Header (kept) ── */}
        <div
          className="header-section"
          style={{
            background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
            borderRadius: 24,
            padding: 32,
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            flexWrap: 'wrap',
            gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 16,
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-mobile" style={{ fontSize: 18, color: 'white' }} />
            </div>
            <div>
              <h1 style={{ color: 'white', fontSize: 28, fontWeight: 600, margin: '0 0 8px 0' }}>
                App Versions Manager
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    background: 'rgba(74,222,128,0.9)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {totalRecords} Total
                </span>
                <span
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {playstoreCount} Play Store
                </span>
                <span
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {nonPlaystoreCount} Non Play Store
                </span>
                <span
                  style={{
                    background: 'rgba(74,222,128,0.7)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {releasedCount} Released
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleOpenAdd}
            style={{
              background: 'white',
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              cursor: 'pointer',
              color: '#2855ae',
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            <i className="pi pi-plus" style={{ fontSize: 14 }} />
            Add New Version
          </button>
        </div>

        {/* ── Stat Cards ── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          {statCards.map((card) => (
            <div
              key={card.label}
              style={{
                background: 'white',
                borderRadius: 16,
                padding: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                border: '1px solid #e5e7eb',
                transition: 'all .2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.06)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: card.tint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className={`pi ${card.icon}`} style={{ fontSize: 16, color: card.iconColor }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>{card.label}</div>
                  <div style={{ fontSize: 24, color: '#1f2937', fontWeight: 700, lineHeight: 1.2 }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Data Table ── */}
        <div
          className="table-section"
          style={{
            background: 'white',
            borderRadius: 20,
            boxShadow: '0 2px 12px rgba(0,0,0,.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            marginBottom: 24,
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid #f3f4f6',
              background: 'linear-gradient(135deg,#ffffff,#fafbfc)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-list" style={{ fontSize: 14, color: '#4169E1' }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>Versions Directory</div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  {totalRecords > 0
                    ? `Showing ${Math.min((page - 1) * pageSize + 1, totalRecords)}–${Math.min(page * pageSize, totalRecords)} of ${totalRecords} versions`
                    : 'Manage mobile app version releases'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={fetchVersions}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: 'white',
                  color: '#4169E1',
                  border: '1.5px solid #c7d2fe',
                  transition: 'all .2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                Refresh
              </button>
              {totalPages > 0 && totalRecords > 0 && (
                <div
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg,rgba(65,105,225,.1),rgba(91,124,230,.08))',
                    border: '1px solid rgba(65,105,225,.15)',
                  }}
                >
                  <span style={{ fontSize: 13, color: '#4169E1', fontWeight: 600 }}>
                    Page {page} of {totalPages}
                  </span>
                </div>
              )}
            </div>
          </div>

          <DataTable
            value={versions}
            loading={loading}
            dataKey="id"
            emptyMessage={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '60px 20px',
                  minHeight: 280,
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    background: 'linear-gradient(135deg,#1e40af,#3b82f6,#60a5fa)',
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 20,
                    boxShadow: '0 6px 18px rgba(59,130,246,.2)',
                  }}
                >
                  <i className="pi pi-mobile" style={{ fontSize: 25, color: 'white' }} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    color: '#1e40af',
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 8,
                    textAlign: 'center',
                  }}
                >
                  No Versions Found
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: '#64748b',
                    fontSize: 14,
                    fontWeight: 500,
                    textAlign: 'center',
                    maxWidth: 360,
                    lineHeight: 1.6,
                  }}
                >
                  Click “Add New Version” to create your first app version.
                </p>
              </div>
            }
          >
            <Column header="Version Name" body={versionNameTemplate} style={{ width: '14%' }} />
            <Column header="Code" body={versionCodeTemplate} style={{ width: '8%' }} />
            <Column header="App Type" body={appTypeTemplate} style={{ width: '13%' }} />
            <Column header="Status" body={releaseStatusTemplate} style={{ width: '13%' }} />
            <Column header="Package URL" body={packageUrlTemplate} style={{ width: '18%' }} />
            <Column header="Notes" body={releaseNotesTemplate} style={{ width: '12%' }} />
            <Column header="Created" body={createdAtTemplate} style={{ width: '10%' }} />
            <Column header="Actions" body={actionsTemplate} style={{ width: '12%' }} />
          </DataTable>

          {totalPages > 1 && (
            <CustomPaginator
              page={page}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
              showInfoBar={false}
            />
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Dialog
        visible={showModal}
        onHide={handleModalClose}
        style={{ width: '520px' }}
        modal
        draggable={false}
        header={editingVersion ? `Edit Version — ${editingVersion.version_name}` : 'Add New App Version'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label
                style={{
                  display: 'block',
                  color: '#2855ae',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                App Type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <Dropdown
                value={formData.app_type}
                options={APP_TYPE_OPTIONS}
                onChange={(e) => handleInputChange('app_type', e.value as string)}
                placeholder="Select type"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  color: '#2855ae',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                Release Update?
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: formData.is_update_released ? '#f0fdf4' : '#f9fafb',
                  border: `2px solid ${formData.is_update_released ? '#86efac' : '#e5e7eb'}`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  transition: 'all 0.2s',
                }}
              >
                <InputSwitch
                  checked={formData.is_update_released}
                  onChange={(e) => handleInputChange('is_update_released', e.value as boolean)}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: formData.is_update_released ? '#16a34a' : '#9ca3af',
                  }}
                >
                  {formData.is_update_released ? 'Released' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                color: '#2855ae',
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Version Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <InputText
              value={formData.version_name}
              onChange={(e) => handleInputChange('version_name', e.target.value)}
              placeholder="e.g., 1.0.2"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                color: '#2855ae',
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Version Code <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <InputNumber
              value={formData.version_code}
              onValueChange={(e) => handleInputChange('version_code', e.value ?? null)}
              placeholder="e.g., 105"
              showButtons={false}
              useGrouping={false}
              min={1}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                color: '#2855ae',
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Package URL <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <InputText
              value={formData.package_url}
              onChange={(e) => handleInputChange('package_url', e.target.value)}
              placeholder="https://example.com/app-v1.0.2.apk"
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                color: '#2855ae',
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              Release Notes
            </label>
            <InputTextarea
              value={formData.release_notes}
              onChange={(e) => handleInputChange('release_notes', e.target.value)}
              placeholder="What's new in this version..."
              rows={4}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button
              label="Cancel"
              onClick={handleModalClose}
              disabled={isSubmitting}
              style={{
                background: '#f3f4f6',
                border: 'none',
                color: '#6b7280',
                padding: '10px 20px',
                borderRadius: 10,
                fontWeight: 600,
              }}
            />
            <Button
              label={isSubmitting ? 'Saving...' : editingVersion ? 'Update Version' : 'Add Version'}
              onClick={handleSubmit}
              disabled={isSubmitting}
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : editingVersion ? 'pi pi-check' : 'pi pi-plus'}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 10,
                fontWeight: 600,
              }}
            />
          </div>
        </div>
      </Dialog>

      {/* ── Delete Confirm Modal ── */}
      <Dialog
        visible={Boolean(deleteTarget)}
        onHide={() => setDeleteTarget(null)}
        style={{ width: '420px' }}
        modal
        draggable={false}
        header="Confirm Delete"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                background: '#fef2f2',
                borderRadius: '50%',
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="pi pi-exclamation-triangle" style={{ fontSize: 24, color: '#ef4444' }} />
            </div>
            <div>
              <p style={{ margin: 0, color: '#111827', fontWeight: 600, fontSize: 15 }}>
                Delete version <span style={{ color: '#ef4444' }}>{deleteTarget?.version_name}</span>?
              </p>
              <p style={{ margin: '6px 0 0 0', color: '#6b7280', fontSize: 13 }}>
                This action cannot be undone. This version will be permanently removed.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              label="Cancel"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
              style={{
                background: '#f3f4f6',
                border: 'none',
                color: '#6b7280',
                padding: '10px 20px',
                borderRadius: 10,
                fontWeight: 600,
              }}
            />
            <Button
              label={isDeleting ? 'Deleting...' : 'Yes, Delete'}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              icon={isDeleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 10,
                fontWeight: 600,
              }}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}